/**
 * ============================================================================
 * ⭐ "POSSIBLE NO-SHOWS (N)" — the board chip and its list (Decision 266)
 * ============================================================================
 *
 * > Pete, 2026-09-28: *"Flag on the board + daily list"* · *"Number 1 but aren't
 * > these called No-Show's?"* → *"Possible no-shows (N)"; No-show is the main
 * > button* · *"Arrival time; door optional"*.
 *
 * ⭐ WHY "POSSIBLE". An appointment whose slot ended with no arrival is EITHER a
 * truck that never came OR one that came and nobody checked in — at Denver 64 of
 * 119 past appointments did arrive. The app cannot tell; a person can. So nothing
 * changes until someone presses a button, and No-show is the main one because it
 * is the likelier answer.
 *
 * ⭐ WHY IT MATTERS BEYOND TIDINESS: every unmarked arrival is a Dock-to-Stock
 * clock that can never start (KPI spec §6: 117 of them on 2026-09-28).
 *
 * ⛔ THE LIST IS THE SERVER'S, NOT THE BOARD'S. It covers every past day at this
 * facility, not just the day on screen, and the server bounds it to the
 * facility's company (§0.0). The board's own red mark uses the same rule
 * (`lib/possibleNoShow.ts`) for the chips it happens to be showing.
 *
 * ⚠️ HIDDEN AT ZERO. A permanent "Possible no-shows (0)" is furniture.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlarmClockOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DirectionTag } from '@/components/DirectionTag';
import { OpenItemChip } from '@/components/OpenItemChip';
import { cn } from '@/lib/utils';
import type { PossibleNoShowListDto, PossibleNoShowRowDto } from '@/api/types';
import {
    listPossibleNoShows, markPossibleNoShowNoShow, markPossibleNoShowArrived,
} from '@/api/client';

/** How often the chip re-counts while the board is open. */
const REFRESH_MS = 5 * 60 * 1000;

/** `<input type="datetime-local">` wants local wall-clock "YYYY-MM-DDTHH:mm". */
function toLocalInput(iso: string): string {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function whenLabel(r: PossibleNoShowRowDto): string {
    const d = new Date(r.scheduledStartUtc);
    const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    if (r.schedulingMode === 'DayOnly') return `${day} · no time`;
    return `${day} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function PossibleNoShowsChip({ facilityCode, getToken, onChanged }: {
    facilityCode: string | null;
    getToken: () => Promise<string>;
    /** Called after a row is marked, so the board reloads. */
    onChanged?: () => void;
}) {
    const [data, setData] = useState<PossibleNoShowListDto | null>(null);
    const [open, setOpen] = useState(false);
    const [loadErr, setLoadErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!facilityCode) { setData(null); return; }
        try {
            const d = await listPossibleNoShows(await getToken(), facilityCode);
            setData(d);
            setLoadErr(null);
        } catch (e) {
            setLoadErr(errText(e));
        }
    }, [facilityCode, getToken]);

    useEffect(() => {
        void load();
        const t = window.setInterval(() => { void load(); }, REFRESH_MS);
        const onFocus = () => { void load(); };
        window.addEventListener('focus', onFocus);
        return () => { window.clearInterval(t); window.removeEventListener('focus', onFocus); };
    }, [load]);

    const total = data?.total ?? 0;
    if (!facilityCode || (total === 0 && !open)) return null;

    return (
        <>
            {/* ⭐ F106 — the app's warning tone, shared with the other open-item chips. */}
            <OpenItemChip
                onClick={() => { setOpen(true); void load(); }}
                title="Past appointments with no arrival recorded — mark each No-show, or save the time it arrived"
                icon={<AlarmClockOff className="h-3 w-3" aria-hidden="true" />}
            >
                Possible no-shows ({total})
            </OpenItemChip>
            <PossibleNoShowsDialog
                open={open}
                onOpenChange={setOpen}
                data={data}
                loadErr={loadErr}
                facilityCode={facilityCode}
                getToken={getToken}
                onMarked={(id) => {
                    setData((d) => d ? { ...d, total: Math.max(0, d.total - 1), rows: d.rows.filter(r => r.appointmentId !== id) } : d);
                    onChanged?.();
                }}
            />
        </>
    );
}

function PossibleNoShowsDialog({ open, onOpenChange, data, loadErr, facilityCode, getToken, onMarked }: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    data: PossibleNoShowListDto | null;
    loadErr: string | null;
    facilityCode: string;
    getToken: () => Promise<string>;
    onMarked: (appointmentId: number) => void;
}) {
    const rows = data?.rows ?? [];
    const hidden = (data?.total ?? 0) - rows.length;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Possible no-shows ({data?.total ?? 0})</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                    {(data?.noShowGraceMinutes ?? 0) > 0
                        ? `These appointments' slots ended more than ${data?.noShowGraceMinutes} minutes ago (this facility's no-show grace) with no arrival recorded. Mark each one`
                        : "These appointments' times have passed with no arrival recorded. Mark each one"}
                    <span className="font-medium"> No-show</span>, or set the time the truck actually came and press
                    <span className="font-medium"> Save arrival</span>.
                </p>
                {loadErr && <div className="text-xs text-rose-700">The list could not be read: {loadErr}</div>}
                {rows.length === 0 && !loadErr && (
                    <div className="py-6 text-center text-sm text-muted-foreground">Nothing left to mark.</div>
                )}
                {rows.length > 0 && (
                    <div className="max-h-[60vh] divide-y overflow-y-auto rounded border">
                        {rows.map((r) => (
                            <PossibleNoShowRow key={r.appointmentId} row={r} facilityCode={facilityCode}
                                               getToken={getToken} onMarked={onMarked} />
                        ))}
                    </div>
                )}
                {hidden > 0 && (
                    <div className="text-[11px] text-muted-foreground">
                        Showing the {rows.length} most recent; {hidden} older ones appear as these are marked.
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function PossibleNoShowRow({ row, facilityCode, getToken, onMarked }: {
    row: PossibleNoShowRowDto;
    facilityCode: string;
    getToken: () => Promise<string>;
    onMarked: (appointmentId: number) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    /* ⭐ The best default time: the kiosk check-in when there is one, else the booked start. */
    const defaultArrival = useMemo(() => toLocalInput(row.checkedInUtc ?? row.scheduledStartUtc), [row]);
    const [arrivalLocal, setArrivalLocal] = useState(defaultArrival);

    const act = async (fn: () => Promise<unknown>) => {
        setBusy(true); setErr(null);
        try {
            await fn();
            onMarked(row.appointmentId);
        } catch (e) {
            setErr(errText(e));
        } finally {
            setBusy(false);
        }
    };

    const direction = row.direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND';
    const ref = [row.poNumber && `PO ${row.poNumber}`, row.referenceNumber && `Ref ${row.referenceNumber}`]
        .filter(Boolean).join(' · ');

    return (
        <div className="flex flex-col gap-1 px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
                <DirectionTag direction={direction} />
                <span className="font-medium">{whenLabel(row)}</span>
                <span className="text-muted-foreground">{row.appointmentNumber}</span>
                <span className="min-w-0 truncate">
                    {[row.clientName || row.clientCode, row.carrierName || row.carrierCode].filter(Boolean).join(' · ')}
                </span>
                <span className="ml-auto flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="destructive" disabled={busy}
                            onClick={() => void act(async () => markPossibleNoShowNoShow(await getToken(), facilityCode, row.appointmentId))}>
                        No-show
                    </Button>
                </span>
            </div>
            {(ref || row.doorCode || row.checkedInUtc) && (
                <div className="text-[11px] text-muted-foreground">
                    {[ref, row.doorCode && `Door ${row.doorCode}`,
                      row.checkedInUtc && `Driver checked in ${new Date(row.checkedInUtc).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`]
                        .filter(Boolean).join(' · ')}
                </div>
            )}
            {/* ⭐ F128 (Decision 306): always open — Pete, 2026-09-29: *"just have the no-show
                button and the arrived at line open below it. No need for the Arrived….. button"*. */}
            <div className={cn('flex items-center gap-2 rounded bg-muted/50 px-2 py-1')}>
                <span>Arrived at</span>
                <input
                    type="datetime-local"
                    className="rounded border px-1 py-0.5 text-xs"
                    value={arrivalLocal}
                    onChange={(e) => setArrivalLocal(e.target.value)}
                />
                <Button size="sm" disabled={busy || !arrivalLocal}
                        onClick={() => void act(async () => markPossibleNoShowArrived(
                            await getToken(), facilityCode, row.appointmentId, new Date(arrivalLocal).toISOString()))}>
                    Save arrival
                </Button>
                <span className="text-[11px] text-muted-foreground">Door optional · recorded as a late entry</span>
            </div>
            {err && <div className="text-[11px] text-rose-700">{err}</div>}
        </div>
    );
}
